import { useEffect, useMemo, useRef, useState } from 'react';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import { api } from '../../lib/api.js';
import PeoplePicker, { pickedId, pillError } from './PeoplePicker.jsx';
import { asAnswer } from './my-review-adapter.js';
import { WEEKDAYS } from '../../lib/access-review-format.js';
import { ACCESS_REVIEW_DEFAULT_ROLES, CADENCES, CADENCE_WORDS, EMAIL_BUDGET, MAX_EXPECTED, WINDOW_MAX, WINDOW_MIN, validateSettings } from '../../../../shared/access-reviews.mjs';

// Never more often than quarterly (Hugo Andrade's email budget, 2026-10-26): the options are the shared CADENCES.
const CADENCE_OPTIONS = Object.freeze(['quarterly', 'semiannual', 'annual', 'off'].filter((c) => Object.hasOwn(CADENCES, c)));
export const BUDGET_LINE = `onGuard never reviews more often than quarterly and sends each reviewer at most ${EMAIL_BUDGET.perReviewerPerCampaign} emails per review (invitation, one reminder, a final reminder 2 days before due, one overdue notice), and at most one access review email a day to any address.`;
const cadenceLabel = (c) => (c === 'off' ? 'Not scheduled (start by hand)' : CADENCE_WORDS[c].replace(/^every/, 'Every'));
const TH = 'border-b border-ipc-border px-2 py-2 text-left text-[12.5px] font-semibold text-ipc-muted';
const TD = 'border-b border-ipc-border px-2 py-2 align-middle';

export const UNNAMED_REVIEWER = 'Designated reviewer (name not available)';

/**
 * The pill of a saved designated reviewer, from the GET (directoryId, and for the admin displayName, mail and upn). A
 * reviewer whose name could not be read is still a pill, never a blank field: left as loaded it stays unchanged, and
 * removing the pill is the only way to remove the reviewer.
 */
export function savedReviewerPill(r) {
  if (typeof r?.directoryId !== 'string' || r.directoryId === '') return null;
  return { key: `id:${r.directoryId}`, directoryUserId: r.directoryId, displayName: r.displayName || UNNAMED_REVIEWER, mail: r.mail || r.upn || null, hasAccount: null, error: null };
}

const sameId = (a, b) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

export const REVIEWER_SET = 'Reviewer set';

function initialForm(settings, names) {
  const reviewers = {};
  for (const [id, r] of Object.entries(settings?.reviewers ?? {})) {
    if (names) { const pill = savedReviewerPill(r); reviewers[id] = pill ? [pill] : []; }
    // A caller who does not see names learns only that a reviewer is set: "Reviewer set" with Clear, or a new address.
    else reviewers[id] = { set: r?.set === true || typeof r?.directoryId === 'string', text: '' };
  }
  const expected = {};
  for (const [id, n] of Object.entries(settings?.expected ?? {})) if (Number.isInteger(n)) expected[id] = String(n);
  return {
    cadence: CADENCE_OPTIONS.includes(settings?.schedule?.cadence) ? settings.schedule.cadence : 'quarterly',
    weekday: settings?.schedule?.weekday ?? 1,
    windowDays: String(settings?.schedule?.windowDays ?? 14),
    mode: settings?.roles?.mode ?? 'default',
    custom: new Set(settings?.roles?.roleTemplateIds ?? []),
    reviewers,
    expected,
  };
}

/**
 * Setup (spec 6.2, 6.3; Hugo Andrade, 2026-10-26): the schedule, the roles in scope ("Every directory role" one click
 * away), a designated reviewer per role picked from the customer's directory (a pill; onGuard saves the directory id,
 * never typed text), and an optional expected count per role. Expected counts are blank by default: nothing is prefilled unless the admin set one.
 * `mode` 'start' saves and then starts the first campaign (one more click from "Start review now").
 */
export default function SetupDialog({ open, mode = 'edit', focusRole = null, onClose, customerId, settings, roles, returnFocusRef, onSaved, names = true }) {
  const [form, setForm] = useState(() => initialForm(settings, names));
  const [state, setState] = useState({ busy: false, error: null });
  const focusRef = useRef(null);
  useEffect(() => { if (open) { setForm(initialForm(settings, names)); setState({ busy: false, error: null }); } }, [open, settings, names]);

  const tenantRoles = useMemo(() => {
    const byId = new Map(roles.map((r) => [r.roleTemplateId, r]));
    const defaults = ACCESS_REVIEW_DEFAULT_ROLES.map((d) => ({ roleTemplateId: d.id, name: byId.get(d.id)?.name ?? d.name, holders: byId.get(d.id)?.holders ?? 0 }));
    const all = [...defaults, ...roles.filter((r) => !ACCESS_REVIEW_DEFAULT_ROLES.some((d) => d.id === r.roleTemplateId))];
    return { defaults, all };
  }, [roles]);
  const listed = form.mode === 'default' ? tenantRoles.defaults : tenantRoles.all;
  const inScope = (id) => form.mode !== 'custom' || form.custom.has(id);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRoleField = (k, id, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [id]: v } }));
  const toggleCustom = (id) => setForm((f) => { const c = new Set(f.custom); if (c.has(id)) c.delete(id); else c.add(id); return { ...f, custom: c }; });

  const buildBody = () => {
    const reviewers = {};
    const expected = {};
    for (const r of tenantRoles.all) {
      const id = r.roleTemplateId;
      if (!names) {
        // Staff acting for the customer type a sign-in name or email (the server resolves it). Left as loaded means
        // unchanged; Clear (and nothing typed) removes the reviewer.
        const f = form.reviewers[id] ?? { set: false, text: '' };
        const wasSet = settings?.reviewers?.[id] != null;
        const typed = f.text.trim();
        if (typed) reviewers[id] = { lookup: typed };
        else if (wasSet && !f.set) reviewers[id] = null;
      } else {
      // A pill left as loaded is unchanged; no pill removes the reviewer; a new pill sends its directory id only.
      const pills = form.reviewers[id] ?? [];
      const wrong = pillError(pills);
      if (wrong) return { error: `Reviewer for ${r.name}: ${wrong} Remove it or pick someone else.` };
      const before = settings?.reviewers?.[id]?.directoryId ?? null;
      const now = pickedId(pills);
      if (!sameId(now, before)) reviewers[id] = now === null ? null : { directoryId: now };
      }
      const was = Number.isInteger(settings?.expected?.[id]) ? String(settings.expected[id]) : '';
      const text = (form.expected[id] ?? '').trim();
      if (text === was) continue;
      if (text === '') { expected[id] = null; continue; }
      if (!/^\d{1,3}$/.test(text)) return { error: `Expected holders for ${r.name} is a whole number from 0 to ${MAX_EXPECTED}, or blank for no expectation.` };
      expected[id] = Number(text);
    }
    const windowDays = Number(form.windowDays);
    const body = {
      schedule: { cadence: form.cadence, weekday: Number(form.weekday), windowDays: Number.isInteger(windowDays) ? windowDays : NaN },
      roles: { mode: form.mode, roleTemplateIds: form.mode === 'custom' ? [...form.custom] : [] },
      ...(Object.keys(reviewers).length ? { reviewers } : {}),
      ...(Object.keys(expected).length ? { expected } : {}),
      version: settings?.version ?? 0,
    };
    const v = validateSettings(body);
    return v.ok ? { body } : { error: v.error };
  };

  const save = async () => {
    const out = buildBody();
    if (out.error) { setState({ busy: false, error: out.error }); return; }
    setState({ busy: true, error: null });
    try {
      await api.saveAccessReviewSettings(customerId, out.body);
      setState({ busy: false, error: null });
      await onSaved(mode);
    } catch (e) {
      const message = e?.status === 409 ? 'Someone changed the setup a moment ago. Close this, reload the page and try again.' : (e?.body?.message ?? e?.message ?? 'The setup was not saved. Try again.');
      setState({ busy: false, error: message });
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose} returnFocusRef={returnFocusRef} wide initialFocusRef={focusRole ? focusRef : null}
      title={mode === 'start' ? 'Start your first access review' : 'Access review setup'}
      description={mode === 'start'
        ? 'These are the recommended defaults. Check the reviewers, then start: each reviewer gets one email with their assignments.'
        : 'How often to review, which roles, and who reviews each role. Without a named reviewer, the manager of the person decides, then the onGuard admins.'}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={state.busy} onClick={save} data-testid="access-reviews-setup-save">
            {state.busy ? 'Saving' : mode === 'start' ? 'Save and start review' : 'Save setup'}
          </button>
        </>
      )}
    >
      <fieldset className="grid gap-4 sm:grid-cols-3">
        <legend className="mb-2 font-semibold">Schedule</legend>
        <label className="block">
          <span className="label">How often</span>
          <select className="input" value={form.cadence} onChange={(e) => setField('cadence', e.target.value)} data-testid="access-reviews-setup-cadence">
            {CADENCE_OPTIONS.map((c) => <option key={c} value={c}>{cadenceLabel(c)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label">Starts on the second</span>
          <select className="input" value={form.weekday} onChange={(e) => setField('weekday', Number(e.target.value))} disabled={form.cadence === 'off'}>
            {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label">Days to decide</span>
          <input type="number" className="input" min={WINDOW_MIN} max={WINDOW_MAX} value={form.windowDays} onChange={(e) => setField('windowDays', e.target.value)} data-testid="access-reviews-setup-window" />
        </label>
        <p className="text-[13px] text-ipc-muted sm:col-span-3" data-testid="access-reviews-setup-budget">{BUDGET_LINE}</p>
      </fieldset>

      <fieldset>
        <legend className="mb-2 font-semibold">Roles to review</legend>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2">
            <input type="radio" name="roles-mode" checked={form.mode === 'default'} onChange={() => setField('mode', 'default')} />
            The privileged roles ({tenantRoles.defaults.length} roles, recommended)
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="roles-mode" checked={form.mode === 'every'} onChange={() => setField('mode', 'every')} data-testid="access-reviews-setup-every" />
            Every directory role ({tenantRoles.all.length} roles in your tenant)
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="roles-mode" checked={form.mode === 'custom'} onChange={() => setField('mode', 'custom')} />
            Choose roles
          </label>
        </div>
      </fieldset>

      <div className="-mx-2 overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid="access-reviews-setup-roles">
          <caption className="sr-only">Reviewer and expected holders per role</caption>
          <thead>
            <tr>
              {form.mode === 'custom' ? <th scope="col" className={TH}><span className="sr-only">Review</span></th> : null}
              <th scope="col" className={TH}>Role</th>
              <th scope="col" className={`${TH} text-right`}>Holders</th>
              <th scope="col" className={TH}>Reviewer (optional)</th>
              <th scope="col" className={TH}>Expected holders (optional)</th>
            </tr>
          </thead>
          <tbody>
            {listed.map((r) => {
              const id = r.roleTemplateId;
              const on = inScope(id);
              return (
                <tr key={id} data-role={id}>
                  {form.mode === 'custom' ? (
                    <td className={TD}><input type="checkbox" checked={form.custom.has(id)} onChange={() => toggleCustom(id)} aria-label={`Review ${r.name}`} /></td>
                  ) : null}
                  <td className={`${TD} font-semibold text-ipc-ink`}>{r.name}</td>
                  <td className={`${TD} text-right tabular-nums`}>{r.holders ?? 0}</td>
                  <td className={TD}>
                    {names ? (
                      <PeoplePicker
                        id={`access-reviews-setup-reviewer-${id}`} label={`Reviewer for ${r.name}`} labelHidden disabled={!on}
                        inputRef={focusRole === id ? focusRef : null}
                        search={(q) => asAnswer(api.searchAccessReviewSetupPeople(customerId, { q, roleId: id }))}
                        value={form.reviewers[id] ?? []} onChange={(pills) => setRoleField('reviewers', id, pills)}
                      />
                    ) : form.reviewers[id]?.set ? (
                      <span className="inline-flex items-center gap-3" data-testid={`access-reviews-setup-reviewer-${id}-set`}>
                        <span className="text-ipc-ink">{REVIEWER_SET}</span>
                        <button
                          type="button" className="tap text-sm font-semibold text-ipc-light hover:underline" disabled={!on}
                          aria-label={`Clear the reviewer for ${r.name}`} onClick={() => setRoleField('reviewers', id, { set: false, text: '' })}
                        >
                          Clear
                        </button>
                      </span>
                    ) : (
                      <input
                        ref={focusRole === id ? focusRef : undefined}
                        type="text" className="input min-w-[12rem]" autoComplete="off" disabled={!on}
                        placeholder="Sign-in name or email" aria-label={`Reviewer for ${r.name}`}
                        value={form.reviewers[id]?.text ?? ''} onChange={(e) => setRoleField('reviewers', id, { set: false, text: e.target.value })}
                      />
                    )}
                  </td>
                  <td className={TD}>
                    <input
                      type="text" inputMode="numeric" className="input w-24" autoComplete="off" disabled={!on}
                      aria-label={`Expected holders for ${r.name} (optional)`}
                      value={form.expected[id] ?? ''} onChange={(e) => setRoleField('expected', id, e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[13px] text-ipc-muted">
        Expected holders is optional. Leave it blank and onGuard only follows the Microsoft guidance for Global Administrators;
        set a number and a finding appears when more people hold the role.
      </p>
      <DialogError message={state.error} />
    </Dialog>
  );
}
